<template>
  <div
    class="app-container"
    :class="appContainerClass"
    :style="appContainerStyle"
  >
    <Sidebar
      v-if="showSidebar"
      :is-sidebar-visible="isSidebarVisible"
      @toggle-sidebar="toggleSidebar"
    />
    <main class="layout-main" :class="layoutMainClass">
      <router-view />
      <canvas id="canvas" v-show="route.meta.showCanvasBG"></canvas>
      <Footer v-if="showFooter" />
    </main>
    <Toast />
    <ConfirmDialog class="app-confirm-dialog" />
    <GlobalSettingsModal />
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, provide, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import Footer from '@/components/Footer.vue';
import Sidebar from '@/components/Sidebar.vue';
import GlobalSettingsModal from '@/features/settings/components/GlobalSettingsModal.vue';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useSidebarLayout } from '@/features/drivers-workspace/composables/useSidebarLayout';
import { SIDEBAR_LAYOUT_KEY } from '@/features/drivers-workspace/constants/sidebarLayout';
import { initBgCanvas } from '@/utils/initBgCanvas';

export default {
  name: 'App',
  components: { Footer, Sidebar, GlobalSettingsModal, Toast },
  setup() {
    const store = useStore();
    const route = useRoute();
    const toast = useToast();
    const sidebarLayout = useSidebarLayout();

    provide(SIDEBAR_LAYOUT_KEY, sidebarLayout);

    const isLogged = computed(() => !!store.state.users?.user?.isLogged);
    const isSidebarVisible = computed(() => store.state.modals.sidebar.isSidebarVisible);

    const showSidebar = computed(() => route.meta.showNavBar && isLogged.value);
    const showFooter = computed(
      () => route.meta.showNavBar && route.meta.showFooter !== false
    );

    const layoutMainClass = computed(() => ({
      'layout-main--plain': !showSidebar.value
    }));

    const appContainerClass = computed(() => ({
      'app-container--with-sidebar': showSidebar.value,
      'sidebar-layout-ready': sidebarLayout.isReady.value
    }));

    const appContainerStyle = computed(() => {
      if (!showSidebar.value) return undefined;
      return {
        '--sidebar-layout-width': sidebarLayout.layoutWidth.value,
        '--sidebar-visual-width': sidebarLayout.visualWidth.value
      };
    });

    const toggleSidebar = () => {
      store.commit('modals/toggleSidebar');
    };

    /** @type {(() => void) | null} */
    let disposeBgCanvas = null;

    const syncBgCanvas = (show) => {
      if (disposeBgCanvas) {
        disposeBgCanvas();
        disposeBgCanvas = null;
      }
      if (show) {
        disposeBgCanvas = initBgCanvas('canvas');
      }
    };

    onMounted(() => {
      const isTheme = store.getters.theme || localStorage.getItem('isTheme') || 'dark';
      if (isTheme === 'dark') {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }

      if (window.innerWidth >= 992) {
        store.commit('modals/setSidebarVisible', true);
      }
    });

    watch(
      () => route.meta.showCanvasBG,
      (show) => syncBgCanvas(!!show),
      { immediate: true }
    );

    onBeforeUnmount(() => {
      syncBgCanvas(false);
    });

    watch(
      () => store.state.users.flashMessage,
      (msg) => {
        if (msg) {
          toast.add({ severity: 'warn', summary: 'Access denied', detail: msg, life: 4000 });
          store.commit('users/clearFlashMessage');
        }
      }
    );

    return {
      route,
      showSidebar,
      showFooter,
      isSidebarVisible,
      layoutMainClass,
      appContainerClass,
      appContainerStyle,
      toggleSidebar
    };
  }
};
</script>

<style>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
}

.app-container:has(.layout-main--plain [data-v-component='LoginView']) {
  overflow: hidden;
  height: 100vh;
}
</style>
